import React, { useState, useEffect } from 'react';
import { UpdateModal } from './UpdateModal';
import './MainMenu.css';

const CURRENT_UPDATE_KEY = 'update_nifc_release_v1';

interface MainMenuProps {
  onNavigate: (view: 'order_rolls' | 'album' | 'inventory' | 'leaderboard' | 'achievements') => void;
  activeRollsCount?: number;
}

const SPLASH_MESSAGES = [
  'Virtual Coin Roll Hunting & Collection Album',
  'Search for Grace Kelly & rare 2€ commemoratives!',
  'Stacking euro rolls from 2002 to present day',
  'Check your pocket change for key dates!',
  'Hunting European central bank coin rolls daily',
  'Build complete commemorative and standard sets!',
  '🎶 Look me in my hunter eyes 🎶',
  'Complete sets & series to earn some bankroll',
  'The lower the mintage, the better the find',
  'Trading with others coming soon!',
  'Maybe check your real wallet for anything cool, too?'
];

export const MainMenu: React.FC<MainMenuProps> = ({ onNavigate, activeRollsCount = 0 }) => {
  const [splashText, setSplashText] = useState<string>(SPLASH_MESSAGES[0]);
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState<boolean>(false);

  useEffect(() => {
    const randomIndex = Math.floor(Math.random() * SPLASH_MESSAGES.length);
    setSplashText(SPLASH_MESSAGES[randomIndex]);

    // Check if the user has already seen this update
    const seen = localStorage.getItem('last_seen_game_update');
    if (seen !== CURRENT_UPDATE_KEY) {
      setIsUpdateModalOpen(true);
    }
  }, []);

  const handleCloseUpdateModal = () => {
    localStorage.setItem('last_seen_game_update', CURRENT_UPDATE_KEY);
    setIsUpdateModalOpen(false);
  };

  const inProgressText = activeRollsCount === 1
    ? '(1 Coin Roll In Progress!)'
    : `(${activeRollsCount} Coin Rolls In Progress!)`;

  return (
    <div className="main-menu-container">
      <div className="main-menu-header">
        <h1 className="main-menu-title">Euro Hunting Simulator</h1>
        <div className="main-menu-subtitle">
          {splashText}
        </div>
      </div>

      <div className="main-menu-cards-list">
        {/* 1. Hunt Coin Rolls */}
        <button
          className="menu-card-btn menu-card-btn-primary"
          onClick={() => onNavigate('order_rolls')}
        >
          <span className="menu-card-icon">🪙</span>
          <div className="menu-card-content">
            <div className="menu-card-title-row">
              <span className="menu-card-title">HUNT COIN ROLLS</span>
              {activeRollsCount > 0 && (
                <span className="menu-card-in-progress">{inProgressText}</span>
              )}
            </div>
            <span className="menu-card-desc">
              Order rolls from European central banks & search for rare finds
            </span>
          </div>
          <span className="menu-card-arrow">→</span>
        </button>

        {/* 2x2 Grid for Other 4 Buttons */}
        <div className="main-menu-grid">
          {/* 2. Collection Album */}
          <button
            className="menu-card-btn"
            onClick={() => onNavigate('album')}
          >
            <span className="menu-card-icon">📖</span>
            <div className="menu-card-content">
              <div className="menu-card-title-row">
                <span className="menu-card-title">COLLECTION ALBUM</span>
              </div>
              <span className="menu-card-desc">
                View your collected euro coins and track country set completions
              </span>
            </div>
            <span className="menu-card-arrow">→</span>
          </button>

          {/* 3. Inventory & Sales */}
          <button
            className="menu-card-btn"
            onClick={() => onNavigate('inventory')}
          >
            <span className="menu-card-icon">💼</span>
            <div className="menu-card-content">
              <div className="menu-card-title-row">
                <span className="menu-card-title">INVENTORY & SALES</span>
              </div>
              <span className="menu-card-desc">
                Manage duplicates, sell rare finds for markup, and deposit commons
              </span>
            </div>
            <span className="menu-card-arrow">→</span>
          </button>

          {/* 4. Achievements */}
          <button
            className="menu-card-btn"
            onClick={() => onNavigate('achievements')}
          >
            <span className="menu-card-icon">🎖️</span>
            <div className="menu-card-content">
              <div className="menu-card-title-row">
                <span className="menu-card-title">ACHIEVEMENTS</span>
              </div>
              <span className="menu-card-desc">
                Reach collection milestones and earn rewards
              </span>
            </div>
            <span className="menu-card-arrow">→</span>
          </button>

          {/* 5. Leaderboard */}
          <button
            className="menu-card-btn"
            onClick={() => onNavigate('leaderboard')}
          >
            <span className="menu-card-icon">🏆</span>
            <div className="menu-card-content">
              <div className="menu-card-title-row">
                <span className="menu-card-title">LEADERBOARD</span>
              </div>
              <span className="menu-card-desc">
                View top rankings across wealth, rarest finds, sets, and throughput
              </span>
            </div>
            <span className="menu-card-arrow">→</span>
          </button>
        </div>
      </div>

      {/* Latest Game Update Modal */}
      <UpdateModal
        isOpen={isUpdateModalOpen}
        onClose={handleCloseUpdateModal}
      />
    </div>
  );
};
